export const enum ThreadSummaryStatus {
  Ok = 'ok',
  Error = 'error',
}

const enum ThreadSummaryVersion {
  V0 = 0,
  V1 = 1,
}

interface ThreadSummaryBase {
  status: ThreadSummaryStatus;
  updated_at: string;
}

/**
 * Legacy flat-text summary. `version` is `0` or absent.
 * Carries the summary as a single `text` blob (optionally mirrored in `content.text`).
 */
interface ThreadSummaryV0 extends ThreadSummaryBase {
  version?: ThreadSummaryVersion.V0;
  text: string;
  content?: { text?: string };
}

/**
 * Structured summary (ME-1136 / ME-3099). `version: 1`, no top-level `text`.
 * `content.title` is a short generated header, `content.summary_bullets` the list.
 */
interface ThreadSummaryV1 extends ThreadSummaryBase {
  version: ThreadSummaryVersion.V1;
  content: {
    title: string;
    summary_bullets: string[];
  };
}

export type ThreadSummary = ThreadSummaryV0 | ThreadSummaryV1;

export interface ParsedThreadSummary {
  bulletPoints: string[];
}

/**
 * Splits a legacy flat-text summary into bullet points, one per non-empty line,
 * stripping any leading bullet markers the backend may have added.
 */
function splitSummaryTextToBullets(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.replace(/^\s*[-•*]\s+/, '').trim())
    .filter((line) => line.length > 0);
}

/**
 * Version-aware normaliser. Dispatches on the `version` discriminant:
 * v1 uses the structured `summary_bullets`; v0 (and any unknown/missing version)
 * falls back to splitting the flat `text`. The generated v1 `title` is intentionally dropped.
 */
export function parseThreadSummary(summary: ThreadSummary): ParsedThreadSummary {
  if (summary.version === ThreadSummaryVersion.V1) {
    return { bulletPoints: summary.content?.summary_bullets ?? [] };
  }

  const text = summary.text ?? summary.content?.text ?? '';

  return { bulletPoints: splitSummaryTextToBullets(text) };
}

/**
 * Guards a raw summary payload before it is stored. A v1 summary is valid when it
 * carries a `summary_bullets` array; a v0 summary when it carries a `text` string
 * (top-level or the optional `content.text` mirror) — matching what
 * {@link parseThreadSummary} is able to read back.
 */
export function isValidThreadSummary(summary: ThreadSummary | null | undefined): summary is ThreadSummary {
  if (!summary) {
    return false;
  }

  if (summary.version === ThreadSummaryVersion.V1) {
    return Array.isArray(summary.content?.summary_bullets);
  }

  return typeof summary.text === 'string' || typeof summary.content?.text === 'string';
}
